/**
 * Gated HITL bridge for `cancel_auto_segmentation`.
 *
 * Same gate as the other proposals (≤1-pending preamble, recoverable
 * `failed` outcomes the model re-asks against, Apply/Reject card): parse the
 * job ID → find it in the viewer's job queue → refuse a job that already
 * finished → a {@link CancelJobProposal} the card renders. Nothing happens
 * until the clinician Applies. Apply re-checks the job is still running and
 * then runs the same steps as the Job Queue panel's Cancel button.
 * Pure/React-free.
 */
import type { JobActionOutcome } from '../../contract';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    reasonOf,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import type { ContourJobFacts } from './jobQueries';

export interface CancelJobDeps extends PendingFlag {
    contourJobs(): ReadonlyArray<ContourJobFacts>;
    /** True while a job status is not finished (Done, Error, Canceled). */
    isRunning(status: string): boolean;
    /** The Job Queue Cancel button's steps. */
    cancel(jobId: string): Promise<void>;
}

export interface CancelJobProposal {
    readonly toolCallId: string;
    readonly jobId: string;
    readonly model: string;
    readonly status: string;
    /** Epoch ms. */
    readonly startedAt: number;
    apply(): Promise<JobActionOutcome>;
    reject(): JobActionOutcome;
    /** No viewport overlay for a job; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export type CancelJobPlan = MutatingPlan<CancelJobProposal>;

export async function planCancelAutoSegmentation(
    call: MutatingToolCall,
    deps: CancelJobDeps
): Promise<CancelJobPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const raw = asRecord(call.input)?.jobId;
    if (typeof raw !== 'string' || !raw) {
        return failedPlan(
            call.toolCallId,
            'invalid cancel_auto_segmentation input'
        );
    }
    const jobId = raw;
    const find = () => deps.contourJobs().find((j) => j.jobId === jobId);
    const job = find();
    if (!job) {
        return failedPlan(
            call.toolCallId,
            `job "${jobId}" was not started in this viewer session; call ` +
                'check_auto_segmentation_jobs and use a jobId it returned'
        );
    }
    if (!deps.isRunning(job.status)) {
        return failedPlan(
            call.toolCallId,
            `job "${jobId}" has already finished (${job.status}); there is ` +
                'nothing to cancel'
        );
    }

    const proposal: CancelJobProposal = {
        toolCallId: call.toolCallId,
        jobId,
        model: job.model,
        status: job.status,
        startedAt: job.createdAt,
        apply: async () => {
            // The job may have finished while the card waited.
            const current = find();
            if (!current || !deps.isRunning(current.status)) {
                return {
                    status: 'failed',
                    reason:
                        'the job finished before it could be cancelled' +
                        (current ? ` (${current.status})` : ''),
                };
            }
            try {
                await deps.cancel(jobId);
            } catch (err) {
                return { status: 'failed', reason: reasonOf(err) };
            }
            return { status: 'applied', jobId, model: job.model };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
